/**
 * RoomQuickNaming — V14.96 : nommer chaque pièce en un clic.
 *  • <QuickNameChips>  : chips de suggestion sous une pièce (surface + déjà nommées).
 *  • <QuickNamingBanner> : « N pièces sans nom → Nommer rapidement » — enchaîne
 *    les pièces sans nom (sélection + zoom canvas), un clic = nom + type + suivante.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Wand2, SkipForward, X, CheckCircle2 } from 'lucide-react';
import { usePlanEditor } from './PlanEditorContext';
import { isGenericRoomLabel, roomAreaM2, suggestRoomNames, roomProgramSummary } from '../../../lib/roomNaming';

export const QuickNameChips = ({ room, onPick, compact = false }) => {
  const { rooms, calibration, imgDims } = usePlanEditor();
  const area = roomAreaM2(room, calibration, imgDims);
  const suggestions = useMemo(() => suggestRoomNames(room, rooms, area, compact ? 5 : 8), [room, rooms, area, compact]);
  return (
    <div className="flex flex-wrap gap-1 items-center" data-testid={`room-quickname-${room.id}`}>
      {area != null && (
        <span className="text-[9.5px] text-gray-500 tabular-nums mr-0.5" data-testid={`room-quickname-area-${room.id}`}>{area.toFixed(1)} m² →</span>
      )}
      {suggestions.map((s) => (
        <button
          key={s.label} type="button"
          onClick={(e) => { e.stopPropagation(); onPick(s); }}
          data-testid={`room-quickname-chip-${room.id}-${s.type}`}
          className="text-[10px] px-1.5 py-0.5 rounded-full border transition-colors hover:bg-amber-500/20 hover:border-amber-400/50"
          style={{ borderColor: 'var(--c-border)', color: 'var(--c-text)', background: 'rgba(255,255,255,0.04)' }}
          title={`Nommer « ${s.label} »`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
};

export const QuickNamingBanner = () => {
  const { rooms, selectedRoomId, setSelectedRoomId, actions } = usePlanEditor();
  const [active, setActive] = useState(false);
  const unnamed = useMemo(() => rooms.filter((r) => isGenericRoomLabel(r.label)), [rooms]);
  const program = useMemo(() => roomProgramSummary(rooms), [rooms]);

  const goNext = useCallback((exceptId) => {
    const next = rooms.find((r) => r.id !== exceptId && isGenericRoomLabel(r.label));
    if (next) { setSelectedRoomId(next.id); return true; }
    return false;
  }, [rooms, setSelectedRoomId]);

  const start = () => {
    setActive(true);
    if (!goNext(null)) setActive(false);
  };

  // Fin automatique quand tout est nommé.
  useEffect(() => {
    if (active && unnamed.length === 0) {
      setActive(false);
      toast.success('Programme complet', { description: program.join(' · ') || `${rooms.length} pièce(s) nommée(s).` });
    }
  }, [active, unnamed.length, program, rooms.length]);

  const current = active ? rooms.find((r) => r.id === selectedRoomId && isGenericRoomLabel(r.label)) : null;
  useEffect(() => {
    if (active && !current) goNext(null);
  }, [active, current, goNext]);

  const pick = (s) => {
    if (!current) return;
    actions.updateRoom(current.id, { label: s.label, type: s.type });
    goNext(current.id);
  };

  if (rooms.length === 0) return null;
  return (
    <div className="mt-2 rounded-lg px-2 py-1.5 text-[10.5px]"
         style={{ background: unnamed.length ? 'rgba(217,160,91,0.08)' : 'rgba(34,197,94,0.08)', border: `1px solid ${unnamed.length ? 'rgba(217,160,91,0.3)' : 'rgba(34,197,94,0.3)'}` }}
         data-testid="rooms-quicknaming-banner" data-unnamed={unnamed.length}>
      {unnamed.length === 0 ? (
        <div className="flex items-start gap-1.5 text-emerald-300/90">
          <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
          <span><strong>Programme :</strong> {program.join(' · ')}</span>
        </div>
      ) : !active ? (
        <div className="flex items-center gap-2">
          <span className="flex-1" style={{ color: 'var(--c-text)' }}>
            <strong>{unnamed.length}</strong> pièce{unnamed.length > 1 ? 's' : ''} sans nom — l'étape « Meubler » a besoin d'un vrai programme.
          </span>
          <button type="button" onClick={start} data-testid="rooms-quicknaming-start"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold text-[10.5px]"
                  style={{ background: 'var(--c-gold)', color: '#0F1115' }}>
            <Wand2 className="w-3 h-3" /> Nommer rapidement
          </button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="flex-1" style={{ color: 'var(--c-text)' }}>
              Pièce <strong>{current?.label || '…'}</strong> surlignée sur le plan — cliquez un nom ({unnamed.length} restante{unnamed.length > 1 ? 's' : ''}) :
            </span>
            <button type="button" onClick={() => current && goNext(current.id)} data-testid="rooms-quicknaming-skip"
                    className="inline-flex items-center gap-1 text-gray-300 hover:text-white" title="Passer cette pièce">
              <SkipForward className="w-3 h-3" /> Passer
            </button>
            <button type="button" onClick={() => setActive(false)} data-testid="rooms-quicknaming-stop"
                    className="text-gray-400 hover:text-white" title="Arrêter"><X className="w-3 h-3" /></button>
          </div>
          {current && <QuickNameChips room={current} onPick={pick} />}
        </div>
      )}
    </div>
  );
};
